import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, IconButton, Tooltip } from "@material-ui/core";
import { Pencil } from "lucide-react";
import { AD } from "./adTokens";

const ICON_INLINE = 14;

export const RetryIcon = ({ color = AD.primary, size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M3 10a7 7 0 0 1 11.9-5M17 10A7 7 0 0 1 5.1 15"
      stroke={color}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M14.6 1.9v3.4h-3.4M5.4 18.1v-3.4h3.4"
      stroke={color}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FailedArtwork = () => (
  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true">
    <path
      d="M22 13h18l11 11v35a3 3 0 0 1-3 3H22a3 3 0 0 1-3-3V16a3 3 0 0 1 3-3Z"
      fill={AD.surface}
      stroke={AD.textHint}
      strokeWidth="1.6"
    />
    <path d="M40 13v11h11" fill="none" stroke={AD.textHint} strokeWidth="1.6" />
    <circle cx="45" cy="47" r="12" fill={AD.decorStrong} />
    <path
      d="m40.8 42.8 8.4 8.4m0-8.4-8.4 8.4"
      stroke={AD.surface}
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <g fill={AD.decorTint}>
      <path d="M14 25.5l1.1 2.4 2.4 1.1-2.4 1.1L14 32.5l-1.1-2.4-2.4-1.1 2.4-1.1L14 25.5Z" />
      <path d="M57 20l.9 2 2 .9-2 .9-.9 2-.9-2-2-.9 2-.9.9-2Z" />
      <path d="M53 33.5l.7 1.5 1.5.7-1.5.7-.7 1.5-.7-1.5-1.5-.7 1.5-.7.7-1.5Z" />
    </g>
  </svg>
);

export const TargetRow = ({ classes, icon, label, onEdit, children }) => (
  <Box className={classes.row}>
    <Box className={classes.tile}>{icon}</Box>
    <Box className={classes.rowBody}>
      <Typography className={classes.rowLabel}>{label}</Typography>
      {children}
    </Box>
    {onEdit ? (
      <Tooltip title={`Edit ${label.toLowerCase()}`}>
        <IconButton
          className={classes.rowEdit}
          size="small"
          onClick={onEdit}
          aria-label={`Edit ${label.toLowerCase()}`}
        >
          <Pencil size={ICON_INLINE} />
        </IconButton>
      </Tooltip>
    ) : null}
  </Box>
);

export const SummaryText = ({ classes, labels, open = false, onToggle }) => {
  const textRef = useRef(null);
  const [clipped, setClipped] = useState(false);
  const text = labels.join(", ");

  useEffect(() => {
    if (open) return undefined;
    const measure = () => {
      const el = textRef.current;
      if (el) setClipped(el.scrollHeight - el.clientHeight > 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, open]);

  return (
    <>
      <Typography
        ref={textRef}
        className={`${classes.rowValue} ${open ? "" : classes.rowValueClamp}`}
      >
        {text}
      </Typography>
      {clipped && onToggle ? (
        <span
          className={classes.moreLink}
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => e.key === "Enter" && onToggle()}
        >
          {open ? "See less" : "See more"}
        </span>
      ) : null}
    </>
  );
};
